<template>
  <div class="kit-material-workbench sku-workbench-view" ref="workbench">
    <div class="sku-workbench">
      <!-- 左侧成品 SKU 侧边栏 -->
      <aside class="sku-sidebar" :style="{ width: skuSidebarWidth + 'px' }">
        <div class="sku-sidebar-table">
          <el-table
            ref="skuSidebarTable"
            v-loading="skuLiveLoading"
            class="shadcn-data-table"
            data-ui="data-table"
            :data="skuRows"
            height="100%"
            row-key="sku"
            highlight-current-row
            empty-text="暂无成品SKU"
            :row-class-name="skuRowClassName"
            @dragstart.native="startSkuRowDrag"
            @dragover.native.prevent="overSkuRowDrag"
            @drop.native="dropSkuRowDrag"
            @dragend.native="endSkuRowDrag"
            @row-click="selectSkuRow">
            <el-table-column label="" width="34" align="center">
              <template slot-scope="scope">
                <el-button class="sku-remove-button shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-delete" title="移除" @click.stop="removeSkuRow(scope.row)"></el-button>
              </template>
            </el-table-column>
            <el-table-column prop="parentProduct" label="SKU" width="118" show-overflow-tooltip>
              <template slot-scope="scope">
                <div class="sku-cell">
                  <span class="sku-order-index">{{ scope.$index + 1 }}</span>
                  <el-tooltip placement="right" effect="light" :open-delay="180" popper-class="sku-bom-tooltip-popper">
                    <div slot="content" class="sku-bom-tooltip">
                      <div class="sku-bom-tooltip__title">{{ scope.row.sku }} 制作组成 (BOM)</div>
                      <div v-if="!skuOptionForRow(scope.row) || skuOptionForRow(scope.row).bomLoading" class="sku-bom-tooltip__empty">正在读取制作组成...</div>
                      <div v-else-if="skuOptionForRow(scope.row).bomError" class="sku-bom-tooltip__empty">制作组成读取失败</div>
                      <div v-else-if="!skuOptionForRow(scope.row).bom.length" class="sku-bom-tooltip__empty">暂无制作组成物料</div>
                      <div v-else class="sku-bom-tooltip__viewport">
                        <div class="sku-bom-tooltip__table">
                          <div class="sku-bom-tooltip__row sku-bom-tooltip__row--head">
                            <span>序号</span><span>物料编码</span><span>物料名称</span><span>单套用量</span>
                          </div>
                          <div v-for="(part, partIndex) in skuOptionForRow(scope.row).bom" :key="part.rowKey" class="sku-bom-tooltip__row">
                            <span>{{ partIndex + 1 }}</span><span>{{ part.matNo }}</span><span>{{ part.matName }}</span><span>{{ part.qtyPer }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span class="sku-text" @mouseenter="loadSkuBom(skuOptionForRow(scope.row))">{{ scope.row.parentProduct || scope.row.sku }}</span>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="成品名称" min-width="130" show-overflow-tooltip></el-table-column>
            <el-table-column prop="exclusiveBuildableQty" label="独占可制作数" width="100" align="center" :formatter="skuRowExclusiveBuildableQty"></el-table-column>
            <el-table-column label="预排产数" width="84" align="center">
              <template slot-scope="scope">
                <el-input
                  v-model="scope.row.planQty"
                  class="sku-plan-qty-input"
                  size="mini"
                  @input="handleSkuPlanQtyInput(scope.row, $event)"
                  @focus="selectSkuRow(scope.row)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="排产结果" width="88" align="center">
              <template slot-scope="scope">
                <el-tag data-ui="badge" size="mini" effect="plain" :type="skuResultTagType(skuRowResults[scope.row.sku])">
                  {{ skuRowResults[scope.row.sku] ? skuRowResults[scope.row.sku].label : '未排产' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="sku-sidebar-footer">
          <el-button class="shadcn-button shadcn-button--primary" type="primary" size="small" icon="el-icon-plus" @click="openSkuCatalog">添加成品SKU</el-button>
        </div>
      </aside>

      <!-- 左右分屏拖拽条 -->
      <div
        class="sku-column-splitter"
        role="separator"
        title="拖动调整左右区域宽度"
        @mousedown="startSkuColumnResize"></div>

      <!-- 右侧三层堆叠工作区 -->
      <div class="sku-detail-stack" ref="skuDetailStack" :style="skuDetailStyle">
        <!-- 第 1 层：选中成品制作组成与可用库存扣减 -->
        <section class="sku-layer" aria-label="选中成品制作组成">
          <div class="sku-layer-head">
            <div class="sku-layer-title">
              <span>选中成品制作组成</span>
              <span v-if="activeSkuItem" class="sku-layer-meta">{{ activeSkuItem.sku }} · {{ activeSkuItem.name }}</span>
              <el-button v-if="activeSkuItem && activeSkuItem.bomError" class="shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-refresh" @click="loadSkuBom(activeSkuItem)">重试 BOM</el-button>
            </div>
            <span class="sku-layer-meta">制作物料 {{ activeSkuBomRows.length }} 项 · 独占可制作 {{ activeSkuItem ? skuExclusiveBuildableQty(activeSkuItem) : 0 }}</span>
          </div>
          <div v-if="skuBomFieldError" role="alert" class="sku-bom-field-error">
            <span>{{ skuBomFieldError }}</span>
            <el-button type="text" size="mini" :loading="skuBomFieldLoading" @click="loadSkuBomFields">重试字段加载</el-button>
          </div>
          <div class="sku-layer-table">
            <el-table
              v-loading="skuBomFieldLoading || Boolean(activeSkuItem && activeSkuItem.bomLoading)"
              class="shadcn-data-table"
              data-ui="data-table"
              :data="activeSkuBomRows"
              height="100%"
              :empty-text="activeSkuItem ? (activeSkuItem.bomError ? '制作组成读取失败' : activeSkuItem.bomLoaded ? 'BOM 无物料明细' : '正在读取制作组成...') : '未选择成品'">
              <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
              <el-table-column v-for="column in skuBomColumns" :key="column.key"
                :prop="column.prop" :label="column.title" :width="column.width"
                :align="column.align" :fixed="column.fixed" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span v-if="column.key === 'SHORTAGEQTY'" :class="scope.row.shortageQty > 0 ? 'danger-text' : 'success-text'">{{ skuBomCellValue(scope.row, column) }}</span>
                  <el-tag v-else-if="column.key === 'ALLOCATIONRESULT'" data-ui="badge" size="mini" effect="plain"
                    :type="scope.row.allocationResult === '欠料' ? 'danger' : scope.row.allocationResult === '齐料' ? 'success' : 'info'">{{ skuBomCellValue(scope.row, column) }}</el-tag>
                  <span v-else>{{ skuBomCellValue(scope.row, column) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>

        <!-- 上下 Splitter 1 -->
        <div
          class="sku-row-splitter"
          role="separator"
          title="拖动调整上下区域高度"
          @mousedown="startSkuLayerResize(0, $event)"></div>

        <!-- 第 2 层：联合排产制作组成与缺料汇总 -->
        <section class="sku-layer" aria-label="联合排产缺料汇总">
          <div class="sku-layer-head">
            <div class="sku-layer-title">
              <span>联合排产制作组成与缺料汇总</span>
              <span class="sku-layer-meta">已填写 {{ skuJointPlanSummary.plannedSkuCount }} 个成品</span>
            </div>
            <div class="sku-layer-actions">
              <span class="sku-layer-meta">欠料成品 {{ skuJointPlanSummary.shortageSkuCount }} · 缺料明细 {{ skuJointPlanSummary.shortageMaterialCount }} 项</span>
              <el-button class="shadcn-button shadcn-button--primary" type="primary" size="mini" icon="el-icon-document-add" :disabled="skuJointPlanSummary.shortageMaterialCount === 0" @click="openSkuProcurement()">新增采购申请单</el-button>
            </div>
          </div>
          <div class="sku-layer-table">
            <el-table class="shadcn-data-table" data-ui="data-table" :data="skuJointShortageRows" height="100%" :empty-text="skuJointResultEmptyText">
              <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
              <el-table-column prop="matNo" label="物料编码" width="108" show-overflow-tooltip></el-table-column>
              <el-table-column prop="matName" label="物料名称" min-width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="unit" label="单位" width="54" align="center"></el-table-column>
              <el-table-column prop="demandQty" label="总需求" width="80" align="right"></el-table-column>
              <el-table-column prop="allocatedQty" label="已分配" width="80" align="right"></el-table-column>
              <el-table-column prop="availableQty" label="可用库存" width="80" align="right"></el-table-column>
              <el-table-column prop="shortageQty" label="总欠料" width="80" align="right">
                <template slot-scope="scope"><span class="danger-text">{{ scope.row.shortageQty }}</span></template>
              </el-table-column>
              <el-table-column prop="sourceSkuLabel" label="涉及成品" min-width="140" show-overflow-tooltip></el-table-column>
              <el-table-column label="操作" width="75" align="center" fixed="right">
                <template slot-scope="scope">
                  <el-button class="shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-plus" title="针对此项缺料生成采购申请" @click.stop="openSkuProcurement(scope.row)">申请</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>

        <!-- 上下 Splitter 2 -->
        <div
          class="sku-row-splitter"
          role="separator"
          title="拖动调整上下区域高度"
          @mousedown="startSkuLayerResize(1, $event)"></div>

        <!-- 第 3 层：全部仓库实时库存现存量 (V_IC_STKLOG) -->
        <section class="sku-layer" aria-label="全部仓库实时库存">
          <div class="sku-layer-head">
            <div class="sku-layer-title">
              <span>全部仓库实时库存</span>
              <span class="sku-layer-meta">共 {{ filteredWarehouseMaterials.length }} 项物料</span>
            </div>
            <div class="sku-layer-actions">
              <el-input
                v-model="warehouseMaterialSearchText"
                class="sku-warehouse-search"
                size="mini"
                placeholder="搜索物料编码、名称、型号、仓库"
                prefix-icon="el-icon-search"
                clearable></el-input>
            </div>
          </div>
          <div class="sku-layer-table">
            <el-table
              v-loading="skuLiveLoading"
              class="shadcn-data-table"
              data-ui="data-table"
              :data="filteredWarehouseMaterials"
              height="100%"
              row-key="rowKey"
              empty-text="暂无仓库物料">
              <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
              <el-table-column prop="matNo" label="物料编码" width="80" show-overflow-tooltip></el-table-column>
              <el-table-column prop="matName" label="物料名称" width="130" show-overflow-tooltip></el-table-column>
              <el-table-column prop="model" label="型号" width="96" show-overflow-tooltip></el-table-column>
              <el-table-column prop="spec" label="规格" min-width="110" show-overflow-tooltip></el-table-column>
              <el-table-column prop="unit" label="单位" width="54" align="center"></el-table-column>
              <el-table-column prop="stockQty" label="物理库存" width="86" align="right"></el-table-column>
              <el-table-column prop="availableQty" label="当前可用量" width="96" align="right">
                <template slot-scope="scope"><strong class="success-text">{{ scope.row.availableQty }}</strong></template>
              </el-table-column>
              <el-table-column prop="warehouse" label="仓库" min-width="110" show-overflow-tooltip></el-table-column>
            </el-table>
          </div>
        </section>
      </div>
    </div>

    <!-- 弹窗：添加成品 SKU 目录 (内置自包含) -->
    <el-dialog title="添加成品制作组成 (SKU)" custom-class="kit-dialog kit-mat-suite-dialog" :visible.sync="skuCatalogDialog" width="960px" append-to-body :close-on-click-modal="false">
      <div class="assembly-catalog-filters" role="search" aria-label="成品查询条件">
        <el-input v-model="skuCatalogFilters.sku" size="mini" placeholder="SKU" clearable prefix-icon="el-icon-search"></el-input>
        <el-input v-model="skuCatalogFilters.name" size="mini" placeholder="成品名称" clearable prefix-icon="el-icon-search"></el-input>
      </div>
      <el-table
        ref="skuCatalogTable"
        class="shadcn-data-table"
        :data="skuCatalogRows"
        border
        size="mini"
        max-height="400"
        row-key="sku"
        highlight-current-row
        :empty-text="skuCatalogEmptyText"
        @row-click="handleSkuCatalogRowClick">
        <el-table-column label="选择" width="60" align="center">
          <template slot-scope="{ row }">
            <el-radio
              :value="skuCatalogSelectedSku"
              :label="row.sku"
              :disabled="skuCatalogConfirmLoading"
              @click.native.stop
              @change="setSkuCatalogSelection(row)">
              <span class="sku-catalog-radio-label">选择 {{ row.sku }}</span>
            </el-radio>
          </template>
        </el-table-column>
        <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
        <el-table-column prop="sku" label="SKU" width="124" show-overflow-tooltip></el-table-column>
        <el-table-column prop="name" label="成品名称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="spec" label="规格型号" min-width="120" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unit" label="单位" width="54" align="center"></el-table-column>
      </el-table>
      <span slot="footer">
        <el-button class="shadcn-button shadcn-button--outline" size="small" :disabled="skuCatalogConfirmLoading" @click="skuCatalogDialog = false">取消</el-button>
        <el-button class="shadcn-button shadcn-button--primary" type="primary" size="small" :loading="skuCatalogConfirmLoading" :disabled="!skuCatalogSelection.length" @click="confirmSkuCatalog">
          {{ skuCatalogConfirmLoading ? '正在读取 BOM...' : ('确认添加' + (skuCatalogSelection.length ? '（' + skuCatalogSelection[0].sku + '）' : '')) }}
        </el-button>
      </span>
    </el-dialog>

    <po-req-dialog
      ref="poReqDialog"
      @success="handlePoReqSuccess">
    </po-req-dialog>
  </div>
</template>

<script>
import { getTabColOrData } from '@/api/user';
import poReqDialog from './po_req_dialog.vue';

const SKU_BOM_FIELD_PROPS = Object.freeze({
  MATNO: 'matNo', MATNAME: 'matName', MODEL: 'model', SPEC: 'spec', UNIT: 'unit',
  QTYPER: 'qtyPer', AVAILABLEBEFORE: 'availableBefore', DEMANDQTY: 'demandQty',
  ALLOCATEDQTY: 'allocatedQty', REMAININGAFTER: 'remainingAfter',
  SHORTAGEQTY: 'shortageQty', ALLOCATIONRESULT: 'allocationResult'
});

/**
 * 规范化成品 SKU 预排产数输入
 * @param {string|number} value 用户输入的数量
 * @returns {number|string} 合法的大于等于0的整数或空
 */
function normalizeSkuPlanQty(value) {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return 0;
  return Math.floor(num);
}

/**
 * 过滤弹窗中的成品 SKU 候选列表
 * @param {Object} row 单条成品记录
 * @param {Object} filters 查询条件（SKU、成品名称）
 * @returns {boolean} 是否符合匹配条件
 */
function matchesSkuBomFilters(row, filters) {
  if (!row || !filters) return true;
  const match = (val, filter) => !filter || String(val || '').toLowerCase().includes(String(filter).toLowerCase());
  return match(row.sku, filters.sku) &&
    match(row.name, filters.name);
}

export default {
  name: 'SkuWork',
  components: { poReqDialog },
  data() {
    return {
      // -------------------------------------------------------------
      // 1. ERP 接口与企业身份参数
      // -------------------------------------------------------------
      v_url: 'xuper_auth/getTableRecMany', // 后端通用多记录查询接口
      v_corpid: this.$cookies ? this.$cookies.get('v_corpid') : '', // 企业账套 ID

      // -------------------------------------------------------------
      // 2. 界面布局与拖拽分屏状态
      // -------------------------------------------------------------
      skuSidebarWidth: 540, // 左侧排产列表宽度 (px)
      skuLayerRatios: [0.34, 0.33, 0.33], // 右侧 3 层工作区高度分配比例 (选中物料 / 联合缺料 / 全部库存)
      activeSku: '', // 当前高亮选中的成品 SKU
      draggingSku: '', // 正在进行拖拽排序的成品 SKU

      // -------------------------------------------------------------
      // 3. 左侧成品排产列表 (工作台核心数据集)
      // -------------------------------------------------------------
      skuRows: [], // 用户已添加的排产成品列表 [{ sku, name, parentProduct, spec, unit, planQty, ... }]

      // -------------------------------------------------------------
      // 4. ERP 实时底表数据与请求控制
      // -------------------------------------------------------------
      skuLiveOptions: [], // 从 V_prd_bom 读取的全部成品 SKU 候选
      skuLiveWarehouseMaterials: [], // 从 V_IC_STKLOG 聚合的全部仓库实时库存物料
      skuLiveLoading: false, // 实时底表接口加载状态 (控制表格 v-loading)
      skuLiveLoaded: false, // 是否已完成初次加载
      skuLiveError: '', // 接口异常信息
      skuLiveStale: false, // 是否为过期的快照数据
      skuLiveRequestId: 0, // 并发请求时序 ID (防止竞态覆盖)
      skuLivePromise: null, // 底表加载 Promise 缓存
      skuBomFieldRows: [],
      skuBomFieldLoading: false,
      skuBomFieldError: '',
      skuBomPromises: {}, // 各成品树状 BOM 制作组成明细的加载 Promise 缓存
      warehouseMaterialSearchText: '', // 第 3 层全厂实时库存物料搜索关键字

      // -------------------------------------------------------------
      // 5. 弹窗状态管理
      // -------------------------------------------------------------
      // (1) 添加成品制作组成 (SKU) 弹窗
      skuCatalogDialog: false, // 弹窗显隐控制
      skuCatalogConfirmLoading: false, // 确认添加按钮异步加载 BOM 时的 loading 状态
      skuCatalogSelection: [], // 弹窗中单选选中的成品项
      skuCatalogFilters: { // 弹窗搜索过滤条件
        sku: '', // SKU 编码
        name: '' // 成品名称
      },


    };
  },
  computed: {
    skuBomColumns() {
      return this.skuBomFieldRows.filter(row => row.THIDE === 'N')
        .slice().sort((a, b) => Number(a.SORTBY || 0) - Number(b.SORTBY || 0))
        .map(row => {
          const key = String(row.key || row.FLDNO || '').toUpperCase();
          return {
            key, prop: SKU_BOM_FIELD_PROPS[key],
            title: this.$translate.getColTitle(row, this.$store.state.lang),
            width: Number(row.width || row.WIDTH || row.COLWIDTH) > 0 ? Number(row.width || row.WIDTH || row.COLWIDTH) : 100,
            align: row.align || row.ALIGN || row.CSS || 'left',
            fixed: row.ISFIX === 'Y' ? 'left' : row.ISFIX === 'R' ? 'right' : false
          };
        }).filter(column => column.key && !['SEL', 'ROWNUM', 'OPER'].includes(column.key));
    },
    // -------------------------------------------------------------
    // 计算属性：成品选项与实时库存
    // -------------------------------------------------------------
    /** 成品 SKU 候选列表 */
    skuOptions() {
      return this.skuLiveOptions;
    },
    /** 全厂仓库实时库存数据 */
    warehouseMaterials() {
      return this.skuLiveWarehouseMaterials;
    },
    /** 第 3 层面板根据关键字过滤后的仓库实时库存列表 (保留真实分仓明细) */
    filteredWarehouseMaterials() {
      const q = (this.warehouseMaterialSearchText || '').trim().toLowerCase();
      if (!q) return this.warehouseMaterials;
      return this.warehouseMaterials.filter(m => (
        String(m.matNo || '').toLowerCase().includes(q) ||
        String(m.matName || '').toLowerCase().includes(q) ||
        String(m.model || '').toLowerCase().includes(q) ||
        String(m.spec || '').toLowerCase().includes(q) ||
        String(m.warehouse || '').toLowerCase().includes(q)
      ));
    },
    /** 右侧 3 层堆叠面板的动态 Grid 布局高度样式 */
    skuDetailStyle() {
      return {
        gridTemplateRows: this.skuLayerRatios.map(r => `minmax(120px, ${Math.max(15, Math.round(r * 100))}fr)`).join(' 9px ')
      };
    },

    // -------------------------------------------------------------
    // 计算属性：当前选中项与第 1 层 BOM 明细
    // -------------------------------------------------------------
    /** 当前高亮聚焦的排产行数据 */
    activeSkuRow() {
      return this.skuRows.find(row => row.sku === this.activeSku) || this.skuRows[0] || null;
    },
    /** 当前高亮聚焦的成品元信息 */
    activeSkuItem() {
      const row = this.activeSkuRow;
      if (!row) return null;
      return this.skuOptions.find(option => option.sku === row.sku) || row;
    },
    /**
     * 第 1 层：当前选中成品的底层原材料明细及在本轮联合排产中的分配结果
     */
    activeSkuBomRows() {
      const row = this.activeSkuRow;
      const item = this.activeSkuItem;
      if (!row || !item || !item.bom || !item.bom.length) return [];
      const planQty = Number(row.planQty) || 0;
      const alloc = this.skuJointAllocation[row.sku] || {};
      return item.bom.map((mat, idx) => {
        const rowKey = mat.rowKey || (mat.matNo + '_' + idx);
        const demand = planQty * (mat.qtyPer || 1);
        const matAlloc = (alloc.materials && alloc.materials[rowKey]) || {};
        const allocated = matAlloc.allocatedQty || 0;
        const availableBefore = matAlloc.availableBefore != null ? matAlloc.availableBefore : this.getWarehouseStock(mat.matNo);
        const shortage = Math.max(0, demand - allocated);
        return {
          ...mat,
          demandQty: demand,
          availableBefore,
          allocatedQty: allocated,
          remainingAfter: Math.max(0, availableBefore - allocated),
          shortageQty: shortage,
          allocationResult: planQty === 0 ? '未排产' : (shortage > 0 ? '欠料' : '齐料')
        };
      });
    },

    // -------------------------------------------------------------
    // 计算属性：核心预运算引擎 (联合库存排产与扣减)
    // -------------------------------------------------------------
    /**
     * 联合库存模拟分配核心计算引擎
     * 规则：按左侧排产列表的先后顺序，依次使用全厂可用库存进行扣减模拟分配
     */
    skuJointAllocation() {
      // 1. 聚合全厂所有仓库各物料的总可用库存池 (仅正数库存可参与分配)
      const stockPool = {};
      this.warehouseMaterials.forEach(m => {
        if (!m.matNo) return;
        stockPool[m.matNo] = (stockPool[m.matNo] || 0) + (Number(m.availableQty) || 0);
      });
      Object.keys(stockPool).forEach(k => {
        stockPool[k] = Math.max(0, stockPool[k]);
      });

      // 2. 依次遍历每个排产成品进行模拟分配
      const allocation = {};
      this.skuRows.forEach(row => {
        const item = this.skuOptions.find(opt => opt.sku === row.sku) || row;
        const planQty = Number(row.planQty) || 0;
        const itemMatAlloc = {};
        let itemShortage = false;

        (item.bom || []).forEach((mat, idx) => {
          const rowKey = mat.rowKey || (mat.matNo + '_' + idx);
          const demand = planQty * (mat.qtyPer || 1);
          // 仅大于 0 的库存可参与扣减分配，负库存视为 0 可用
          const currentAvail = Math.max(0, stockPool[mat.matNo] != null ? stockPool[mat.matNo] : 0);
          const allocQty = Math.min(demand, currentAvail);
          // 扣减库存池中的可用量
          stockPool[mat.matNo] = Math.max(0, currentAvail - allocQty);
          if (demand > allocQty) {
            itemShortage = true;
          }
          itemMatAlloc[rowKey] = {
            availableBefore: currentAvail,
            allocatedQty: allocQty,
            shortageQty: Math.max(0, demand - allocQty)
          };
        });

        allocation[row.sku] = {
          planQty,
          isShortage: planQty > 0 && itemShortage,
          isReady: planQty > 0 && !itemShortage,
          materials: itemMatAlloc
        };
      });
      return allocation;
    },

    /**
     * 左侧排产列表中各行的最终排产结果标签 (未填写 / 齐料 / 欠料 / 无BOM)
     */
    skuRowResults() {
      const results = {};
      this.skuRows.forEach(row => {
        const item = this.skuOptions.find(opt => opt.sku === row.sku) || row;
        const alloc = this.skuJointAllocation[row.sku];
        if (!item || !item.bom || !item.bom.length) {
          results[row.sku] = { label: '无BOM', type: 'info', status: '无BOM' };
        } else if (!alloc || alloc.planQty === 0) {
          results[row.sku] = { label: '未填写', type: 'info', status: '未填写' };
        } else if (alloc.isShortage) {
          results[row.sku] = { label: '欠料', type: 'danger', status: '欠料' };
        } else {
          results[row.sku] = { label: '齐料', type: 'success', status: '齐料' };
        }
      });
      return results;
    },

    /**
     * 第 2 层：汇总所有排产成品的缺料物料清单及涉及的成品 (去重显示涉及成品)
     */
    skuJointShortageRows() {
      const shortageMap = {};
      this.skuRows.forEach(row => {
        const item = this.skuOptions.find(opt => opt.sku === row.sku) || row;
        const alloc = this.skuJointAllocation[row.sku];
        if (!alloc || alloc.planQty === 0) return;
        (item.bom || []).forEach((mat, idx) => {
          const rowKey = mat.rowKey || (mat.matNo + '_' + idx);
          const matAlloc = alloc.materials[rowKey];
          if (matAlloc && matAlloc.shortageQty > 0) {
            if (!shortageMap[mat.matNo]) {
              shortageMap[mat.matNo] = {
                matNo: mat.matNo,
                matName: mat.matName,
                unit: mat.unit,
                demandQty: 0,
                allocatedQty: 0,
                availableQty: Math.max(0, this.getWarehouseStock(mat.matNo)),
                shortageQty: 0,
                sourceSkus: []
              };
            }
            shortageMap[mat.matNo].demandQty += alloc.planQty * (mat.qtyPer || 1);
            shortageMap[mat.matNo].allocatedQty += matAlloc.allocatedQty;
            shortageMap[mat.matNo].shortageQty += matAlloc.shortageQty;
            if (!shortageMap[mat.matNo].sourceSkus.includes(row.sku)) {
              shortageMap[mat.matNo].sourceSkus.push(row.sku);
            }
          }
        });
      });
      return Object.values(shortageMap).map(r => ({
        ...r,
        sourceSkuLabel: Array.from(new Set(r.sourceSkus)).join(', '),
        result: '欠料'
      }));
    },

    /**
     * 联合预排产结果汇总统计 (已填写成品数 / 欠料成品数 / 缺料物料项数)
     */
    skuJointPlanSummary() {
      const planned = this.skuRows.filter(r => Number(r.planQty) > 0);
      const shortage = planned.filter(r => this.skuRowResults[r.sku] && this.skuRowResults[r.sku].status === '欠料');
      return {
        plannedSkuCount: planned.length,
        shortageSkuCount: shortage.length,
        shortageMaterialCount: this.skuJointShortageRows.length
      };
    },

    /** 第 2 层表格无数据时的友好空提示 */
    skuJointResultEmptyText() {
      if (!this.skuRows.some(r => Number(r.planQty) > 0)) return '请在左侧成品列表输入预排产数';
      return this.skuJointShortageRows.length === 0 ? '当前排产无缺料物料' : '暂无数据';
    },

    /** Radio 与确认按钮共用同一份选中状态 */
    skuCatalogSelectedSku() {
      return this.skuCatalogSelection.length ? this.skuCatalogSelection[0].sku : null;
    },

    /** 弹窗中经条件过滤后的成品候选记录 */
    skuCatalogRows() {
      return this.skuOptions.filter(row => matchesSkuBomFilters(row, this.skuCatalogFilters));
    },

    /** 弹窗表格空提示 */
    skuCatalogEmptyText() {
      if (this.skuLiveLoading) return '正在加载成品目录...';
      return '暂无成品目录数据';
    }
  },

  created() {
    this._skuBomFieldsDisposed = false;
    this.loadSkuBomFields();
    // 初始化账套 ID 并加载成品台账底表与全厂实时库存
    this.v_corpid = this.$cookies ? this.$cookies.get('v_corpid') : '';
    this.loadSkuLiveData();
  },

  beforeDestroy() {
    this._skuBomFieldsDisposed = true;
  },

  methods: {
    skuBomCellValue(row, column) {
      const value = column.prop ? row[column.prop] : null;
      return value == null || value === '' ? '—' : value;
    },
    async loadSkuBomFields() {
      if (this._skuBomFieldsDisposed || this.skuBomFieldLoading) return;
      this.skuBomFieldLoading = true;
      this.skuBomFieldError = '';
      this.skuBomFieldRows = [];
      try {
        const res = await getTabColOrData('xuper_auth/getBaseData', 'v_sys_form_fld',
          'idseq', '490050', 'lstseq', '4', '', '', ' order by sortby,lstseq');
        if (this._skuBomFieldsDisposed) return;
        if (!res || String(res.code) !== '200' || !Array.isArray(res.result)) {
          throw new Error((res && (typeof res.result === 'string' ? res.result : res.message)) || '制作组成字段读取失败');
        }
        this.skuBomFieldRows = res.result;
      } catch (error) {
        if (!this._skuBomFieldsDisposed) this.skuBomFieldError = error.message || '制作组成字段读取失败';
      } finally {
        if (!this._skuBomFieldsDisposed) this.skuBomFieldLoading = false;
      }
    },
    // -------------------------------------------------------------
    // 1. 数据加载与 ERP 接口对接
    // -------------------------------------------------------------
    /**
     * 加载成品 BOM 台账 (V_prd_bom) 与全厂仓库实时库存 (V_IC_STKLOG)
     * @param {boolean} force 是否强制重新请求
     */
    loadSkuLiveData(force) {
      if (this.skuLiveLoading) return this.skuLivePromise;
      if (this.skuLiveLoaded && !force) return Promise.resolve();
      const requestId = this.skuLiveRequestId + 1;
      this.skuLiveRequestId = requestId;
      this.skuLiveLoading = true;
      this.skuLiveError = '';
      this.skuLiveStale = Boolean(force && this.skuLiveLoaded);
      const corpid = this.v_corpid;

      const promise = Promise.all([
        // 查询成品 BOM 母件 (V_prd_bom)
        getTabColOrData(
          this.v_url,
          'V_prd_bom',
          '',
          '',
          'corpid',
          corpid,
          '',
          '',
          " and stfg!='X' and ishide='N' order by adddttm desc"
        ),
        // 查询仓库实时库存 (V_IC_STKLOG)
        getTabColOrData(
          this.v_url,
          'V_IC_STKLOG',
          'corpid',
          corpid,
          '',
          '',
          '',
          '',
          ' and qty>0 order by STKNM desc'
        )
      ]).then(([bomRes, stockRes]) => {
        if (requestId !== this.skuLiveRequestId) return;
        const rawBoms = (bomRes && bomRes.code === '200' && Array.isArray(bomRes.result)) ? bomRes.result : [];
        const rawStocks = (stockRes && stockRes.code === '200' && Array.isArray(stockRes.result)) ? stockRes.result : [];

        // 格式化成品母件台账
        this.skuLiveOptions = rawBoms.map(row => ({
          sku: row.MATNO || row.IDNO || '',
          name: row.MATNM || '',
          parentProduct: row.MATNO || '',
          documentNo: row.IDNO || '',
          spec: row.SPECNM || '',
          unit: row.UNITNM || '',
          sourceId: row.IDSEQ || '',
          bom: [],
          bomLoaded: false,
          bomLoading: false,
          bomError: ''
        }));

        // 保留全厂各个仓库的完整物理库存与可用量明细 (不进行单一物料合并压扁，防止分仓信息丢失)
        this.skuLiveWarehouseMaterials = rawStocks.map((item, idx) => ({
          rowKey: (item.MATNO || '') + '_' + (item.STKNM || item.STKNO || '') + '_' + idx,
          matNo: item.MATNO || '',
          matName: item.MATNM || '',
          model: item.MODELNM || '',
          spec: item.SPECNM || item.MATDESC || '',
          unit: item.UNITNM || '',
          stockQty: Number(item.QTY) || 0,
          availableQty: Number(item.OKQTY != null ? item.OKQTY : item.QTY) || 0,
          warehouse: item.STKNM || item.STKNO || '',
          warehouseNo: item.STKNO || ''
        }));

        this.skuLiveLoaded = true;
        this.skuLiveStale = false;
      }).catch(error => {
        if (requestId !== this.skuLiveRequestId) return;
        this.skuLiveError = error && error.message ? error.message : 'ERP 数据读取失败';
        this.$message.error(this.skuLiveError);
      }).finally(() => {
        if (requestId === this.skuLiveRequestId) {
          this.skuLiveLoading = false;
          this.skuLivePromise = null;
        }
      });
      this.skuLivePromise = promise;
      return promise;
    },

    /**
     * 动态加载选定成品的制作组成明细 (V_PRD_BOMDTL Oracle 树状 BOM 递归)
     * 对齐 tree_bom.vue 核心 SQL 逻辑
     * @param {Object} item 目标成品对象
     */
    loadSkuBom(item) {
      if (!item || item.bomLoaded || item.bomLoading) return this.skuBomPromises[item && item.sku];
      if (!item.sourceId && !item.sku) {
        this.$set(item, 'bomLoaded', true);
        this.$set(item, 'bomError', '缺少 BOM IDSEQ/SKU');
        return Promise.resolve();
      }
      this.$set(item, 'bomLoading', true);
      this.$set(item, 'bomError', '');

      // 树状递归穿透查询成品底层的全部原材料定额用量
      const sql = `select * from (SELECT LVL,MATNO,MATNM,MATTYNM,SPECNM,BOMUNITNM,QTY,POSITION,RMK,IDSEQ,LSTSEQ,BOMFLAG,ADDUSER,ADDDTTM FROM V_PRD_BOMDTL START WITH IDSEQ=${item.sourceId} and parentno='${item.sku}' CONNECT BY PRIOR CODE=PARENTNO ) where matno like 'm%'`;
      const promise = this.$axios.post('xuper_auth/getFreeData', {
        p_cond: sql
      }).then(res => {
        const rows = (res && res.code === '200' && Array.isArray(res.result)) ? res.result : [];
        const bomItems = rows.map((dtl, idx) => ({
          rowKey: (dtl.MATNO || '') + '_' + idx,
          matNo: dtl.MATNO || '',
          matName: dtl.MATNM || '',
          model: dtl.MATTYNM || '',
          spec: dtl.SPECNM || '',
          unit: dtl.BOMUNITNM || dtl.UNITNM || '',
          qtyPer: Number(dtl.QTY || 1)
        }));
        this.$set(item, 'bom', bomItems);
        this.$set(item, 'bomLoaded', true);
        return bomItems;
      }).catch(error => {
        this.$set(item, 'bomError', error && error.message ? error.message : '制作组成读取失败');
        return [];
      }).finally(() => {
        this.$set(item, 'bomLoading', false);
        delete this.skuBomPromises[item.sku];
      });

      this.$set(this.skuBomPromises, item.sku, promise);
      return promise;
    },

    // -------------------------------------------------------------
    // 2. 辅助工具与行交互方法
    // -------------------------------------------------------------
    /** 获取某物料在全厂所有仓库的当前可用库存总量 (累加各分仓 availableQty) */
    getWarehouseStock(matNo) {
      if (!matNo) return 0;
      return this.warehouseMaterials
        .filter(x => x.matNo === matNo)
        .reduce((sum, item) => sum + (Number(item.availableQty) || 0), 0);
    },
    /** 根据排产行匹配成品元数据 */
    skuOptionForRow(row) {
      return row ? this.skuOptions.find(opt => opt.sku === row.sku) || row : null;
    },
    /** 表格列 formatter：格式化独占可制作数 */
    skuRowExclusiveBuildableQty(row) {
      const item = row && row.bom && row.bom.length ? row : this.skuOptionForRow(row);
      return this.skuExclusiveBuildableQty(item);
    },
    /** 计算单个成品的独占可制作数 min(可用量 / 单台用量) (合并相同物料单台定额，负库存按0处理) */
    skuExclusiveBuildableQty(item) {
      if (!item || !item.bom || !item.bom.length) return 0;
      // 1. 汇总该 BOM 内部各物料的总单台用量 (合并相同 matNo 的定额)
      const totalMatQtyMap = {};
      item.bom.forEach(part => {
        if (!part.matNo) return;
        totalMatQtyMap[part.matNo] = (totalMatQtyMap[part.matNo] || 0) + (Number(part.qtyPer) || 0);
      });

      const limits = Object.entries(totalMatQtyMap).map(([matNo, totalQtyPer]) => {
        if (totalQtyPer <= 0) return 0;
        // 负库存可用量视为 0，可制作数必须 >= 0
        const stock = Math.max(0, this.getWarehouseStock(matNo));
        return Math.floor(stock / totalQtyPer);
      });
      return limits.length ? Math.max(0, Math.min(...limits)) : 0;
    },
    /** 处理用户输入预排产数 */
    handleSkuPlanQtyInput(row, value) {
      this.$set(row, 'planQty', normalizeSkuPlanQty(value));
    },
    /** 点击排产行：设置当前高亮项，并自动按需加载其制作组成 BOM 明细 */
    selectSkuRow(row) {
      this.activeSku = row.sku;
      const item = this.skuOptionForRow(row);
      if (item && !item.bomLoaded) {
        this.loadSkuBom(item);
      }
    },
    /** 动态设置排产列表行类名 (高亮激活与拖拽目标) */
    skuRowClassName({ row }) {
      const classes = ['sku-draggable-row'];
      if (row.sku === this.activeSku) classes.push('is-drop-target');
      return classes.join(' ');
    },
    /** 根据排产状态返回 Tag 徽章类型 */
    skuResultTagType(result) {
      return !result || ['未填写', '加载中', '无BOM'].indexOf(result.status) >= 0 ? 'info' : result.status === '齐料' ? 'success' : 'danger';
    },
    /** 从排产列表中移除某成品 */
    removeSkuRow(row) {
      this.skuRows = this.skuRows.filter(r => r.sku !== row.sku);
      if (this.activeSku === row.sku && this.skuRows.length) {
        this.activeSku = this.skuRows[0].sku;
      }
    },

    // -------------------------------------------------------------
    // 3. 表格行拖拽排序 (Drag & Drop)
    // -------------------------------------------------------------
    /** 开始拖拽排产行 */
    startSkuRowDrag(e) {
      const tr = e.target.closest('tr');
      if (!tr) return;
      this.draggingSku = tr.__vue__ ? tr.__vue__.row && tr.__vue__.row.sku : '';
      e.dataTransfer.effectAllowed = 'move';
    },
    /** 拖拽悬停 */
    overSkuRowDrag(e) {
      e.preventDefault();
    },
    /** 放置行：完成在排产列表中的位置置换 */
    dropSkuRowDrag(e) {
      const tr = e.target.closest('tr');
      if (!tr) return;
      const targetSku = tr.__vue__ && tr.__vue__.row && tr.__vue__.row.sku;
      if (!this.draggingSku || !targetSku || this.draggingSku === targetSku) return;
      const from = this.skuRows.findIndex(r => r.sku === this.draggingSku);
      const to = this.skuRows.findIndex(r => r.sku === targetSku);
      if (from >= 0 && to >= 0) {
        const item = this.skuRows.splice(from, 1)[0];
        this.skuRows.splice(to, 0, item);
      }
      this.draggingSku = '';
    },
    /** 结束拖拽 */
    endSkuRowDrag() {
      this.draggingSku = '';
    },

    // -------------------------------------------------------------
    // 4. 分屏拖拽与高度调整 Splitter
    // -------------------------------------------------------------
    /** 左右拖拽条：调整左侧排产列表宽度 */
    startSkuColumnResize(event) {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = this.skuSidebarWidth;
      const move = ev => {
        this.skuSidebarWidth = Math.max(300, Math.min(800, startWidth + ev.clientX - startX));
      };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    },
    /** 上下拖拽条：调整右侧 3 层工作区高度分配比例 */
    startSkuLayerResize(index, event) {
      event.preventDefault();
      const startY = event.clientY;
      const startRatios = this.skuLayerRatios.slice();
      const stack = this.$refs.skuDetailStack;
      const usableH = stack ? stack.clientHeight : 600;
      const move = ev => {
        const delta = (ev.clientY - startY) / usableH;
        const next = startRatios.slice();
        next[index] = Math.max(0.15, startRatios[index] + delta);
        next[index + 1] = Math.max(0.15, startRatios[index + 1] - delta);
        const sum = next.reduce((a, b) => a + b, 0);
        this.skuLayerRatios = next.map(x => x / sum);
      };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    },

    // -------------------------------------------------------------
    // 5. 添加成品制作组成 (SKU) 弹窗交互 (单选控制与状态回显)
    // -------------------------------------------------------------
    /** 同步单选数据与高亮；传 null 表示取消选择 */
    setSkuCatalogSelection(row) {
      this.skuCatalogSelection = row ? [row] : [];
      // 等待数据更新和表格内部行点击处理完成后，再同步高亮。
      this.$nextTick(() => {
        const table = this.$refs.skuCatalogTable;
        if (table) table.setCurrentRow(this.skuCatalogSelection[0] || null);
      });
    },
    /** 打开弹窗：清除旧筛选，回显当前激活的 SKU */
    openSkuCatalog() {
      this.skuCatalogFilters.sku = '';
      this.skuCatalogFilters.name = '';
      this.skuCatalogDialog = true;
      const targetSku = this.activeSku || (this.skuRows.length ? this.skuRows[0].sku : '');
      const targetRow = targetSku
        ? this.skuCatalogRows.find(row => row.sku === targetSku)
        : null;
      this.setSkuCatalogSelection(targetRow || null);
    },
    /** 点击整行单选；再次点击已选行取消选择 */
    handleSkuCatalogRowClick(row) {
      if (this.skuCatalogConfirmLoading) {
        this.setSkuCatalogSelection(this.skuCatalogSelection[0] || null);
        return;
      }
      const isSelected = this.skuCatalogSelectedSku === row.sku;
      this.setSkuCatalogSelection(isSelected ? null : row);
    },
    /** 确认添加：按钮 loading，在弹窗内先完成树状 BOM 查询，成功后还原按钮、关闭弹窗、渲染数据 */
    async confirmSkuCatalog() {
      if (!this.skuCatalogSelection.length || this.skuCatalogConfirmLoading) return;
      const selected = this.skuCatalogSelection[0];
      this.skuCatalogConfirmLoading = true;

      let targetItem = this.skuRows.find(r => r.sku === selected.sku);
      if (!targetItem) {
        targetItem = {
          ...selected,
          planQty: '' // 默认预排产数为空，不自动填充
        };
        this.skuRows.push(targetItem);
      }
      this.activeSku = targetItem.sku;

      try {
        // 在弹窗内先行加载树状 BOM 物料明细
        await this.loadSkuBom(targetItem);
        this.skuCatalogConfirmLoading = false;
        this.skuCatalogDialog = false;
      } catch (err) {
        this.skuCatalogConfirmLoading = false;
        this.$message.error('制作组成 BOM 读取失败：' + (err && err.message ? err.message : '请重试'));
      }
    },

    // -------------------------------------------------------------
    // 6. 唤起通用采购申请单弹窗 (po_req_dialog)
    // -------------------------------------------------------------
    /**
     * 打开采购申请单新增弹窗，并注入欠料明细
     * @param {Object} [targetRow] 单行申请时传入的特定行
     */
    openSkuProcurement(targetRow) {
      const rows = targetRow ? [targetRow] : this.skuJointShortageRows;
      if (!rows || !rows.length) {
        this.$message.warning('当前暂无预测欠料物料明细');
        return;
      }
      const plannedSkus = this.skuRows.filter(r => Number(r.planQty) > 0).map(r => r.sku);
      this.$refs.poReqDialog.openWithShortageMaterials(rows, {
        title: '成品预测排产采购申请单',
        deptNo: '生产部',
        remark: plannedSkus.length ? `成品预测排产欠料申请 (涉及成品: ${plannedSkus.join('、')})` : '成品预测排产欠料申请'
      });
    },

    /** 采购申请单创建成功回调 */
    handlePoReqSuccess(res) {
      setTimeout(() => {
        this.fetchLiveWarehouseMaterials();
      }, 1000);
    }
  }
};
</script>

<style scoped>
.sku-bom-field-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  padding: 4px 12px;
  color: #dc2626;
}

.sku-catalog-radio-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.kit-material-workbench {
  --background: #f6f7f9;
  --foreground: #0b1019;
  --card: #ffffff;
  --card-foreground: #0b1019;
  --popover: #ffffff;
  --popover-foreground: #0b1019;
  --primary: #2563eb;
  --primary-foreground: #ffffff;
  --secondary: #f4f4f5;
  --secondary-foreground: #0b1019;
  --muted: #f4f4f5;
  --muted-foreground: #a6abb4;
  --accent: #eff6ff;
  --accent-foreground: #1d4ed8;
  --destructive: #dc2626;
  --destructive-foreground: #ffffff;
  --border: #e4e4e7;
  --input: #d4d4d8;
  --ring: #2563eb;
  --success: #15803d;
  --success-soft: #ecfdf3;
  --warning: #b45309;
  --warning-soft: #fff7ed;
  --shortage-row: #fff1f2;
  --shortage-row-hover: #ffe4e6;
  --radius: 6px;
  --font-size-body: 12px;
  --font-size-table-header: 12px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--background);
  color: var(--foreground);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: var(--font-size-body);
  box-sizing: border-box;
}

.sku-workbench {
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--background);
}

.sku-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 320px;
  max-width: 820px;
  border-right: 1px solid var(--border);
  background: var(--card);
  overflow: hidden;
}

.sku-sidebar-table {
  flex: 1 1 auto;
  min-height: 0;
  height: calc(100% - 46px);
  overflow: hidden;
}

.sku-sidebar-footer {
  flex: 0 0 46px;
  height: 46px;
  min-height: 46px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 12px;
  border-top: 1px solid var(--border);
  background: #fafafa;
}

.sku-column-splitter {
  flex: 0 0 9px;
  cursor: col-resize;
  background: var(--muted);
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
  position: relative;
}
.sku-column-splitter::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 3px;
  width: 3px;
  height: 42px;
  border-radius: 3px;
  background: var(--input);
  transform: translateY(-50%);
}
.sku-column-splitter:hover::after {
  background: var(--primary);
}

.sku-detail-stack {
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--background);
}

.sku-layer {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--card);
  border: 1px solid var(--border);
  overflow: hidden;
}

.sku-layer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 36px;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
  background: #fafafa;
}
.sku-layer-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 12px;
}
.sku-layer-meta {
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 400;
}
.sku-layer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sku-layer-table {
  flex: 1 1 auto;
  min-height: 0;
}

.sku-row-splitter {
  height: 9px;
  cursor: row-resize;
  background: var(--muted);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  position: relative;
}
.sku-row-splitter::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 50%;
  width: 42px;
  height: 3px;
  border-radius: 3px;
  background: var(--input);
  transform: translateX(-50%);
}
.sku-row-splitter:hover::after {
  background: var(--primary);
}

.sku-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sku-order-index {
  color: var(--muted-foreground);
  font-size: 11px;
}
.sku-text {
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
}
.sku-text:hover {
  text-decoration: underline;
}

.sku-plan-qty-input ::v-deep .el-input__inner {
  height: 22px;
  line-height: 22px;
  padding: 0 4px;
  text-align: center;
}

.sku-warehouse-search {
  width: 220px;
}

.sku-catalog-search-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}

.dialog-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid var(--border);
  border-radius: 5px;
}
.dialog-meta span { color: var(--muted-foreground); }
.dialog-meta strong { margin-left: 5px; color: var(--foreground); font-weight: 600; }

.danger-text { color: var(--destructive); font-weight: 600; }
.success-text { color: var(--success); font-weight: 600; }
.warning-text { color: var(--warning); font-weight: 600; }

::v-deep .shadcn-data-table tbody tr { height: 32px; }
::v-deep .shadcn-data-table tbody td { height: 31px; padding: 0 !important; }
::v-deep .shadcn-data-table tbody .cell {
  color: var(--foreground);
  font-size: var(--font-size-body);
  height: 31px;
  line-height: 31px;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
}

::v-deep .shadcn-data-table thead th {
  height: 32px;
  padding: 0 !important;
  vertical-align: middle;
  background: #fff !important;
  font-weight: var(--font-weight-table-header);
  font-size: var(--font-size-table-header);
  color: var(--foreground);
}
::v-deep .shadcn-data-table thead th .cell {
  height: 31px;
  line-height: 31px;
  padding-left: 8px;
  padding-right: 8px;
  white-space: nowrap;
}

::v-deep .shadcn-button {
  height: 30px;
  padding: 0 10px;
  border-radius: var(--radius);
  font-size: 12px;
  font-weight: 550;
}
::v-deep .shadcn-button--primary { color: var(--primary-foreground); background: var(--primary); border-color: var(--primary); }
::v-deep .shadcn-button--outline { color: var(--foreground); background: var(--card); border-color: var(--input); }
::v-deep .shadcn-button--ghost { color: var(--muted-foreground); background: transparent; border-color: transparent; }
::v-deep .shadcn-button--ghost:hover { color: var(--foreground); background: var(--muted); }
</style>


<!-- append-to-body 弹窗必须使用专属类，禁止添加无前缀的全局覆盖。 -->
<style lang="less">
.el-dialog.sku-purchase-request-dialog {
  --sku-req-border: #e2e8f0;
  --sku-req-text: #0f172a;
  --sku-req-muted: #64748b;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 40px);
  max-height: calc(100vh - 24px);
  max-width: calc(100vw - 32px);
  margin: 12px auto;
  overflow: hidden;
  border: 1px solid var(--sku-req-border);
  border-radius: 8px;
  background: #fff;
  color: var(--sku-req-text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);

  > .el-dialog__header {
    flex: none;
    padding: 0;
    border-bottom: 1px solid var(--sku-req-border);
    background: #fff;
  }
  .po-request-title {
    height: 48px !important;
    padding: 0 48px 0 16px;
    display: flex;
    align-items: center;
    background: #fff !important;
    color: var(--sku-req-text) !important;
    font-weight: 600;
  }
  > .el-dialog__header .el-dialog__headerbtn {
    top: 12px !important;
    right: 14px;
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 4px;
    background: transparent;
    font-size: 18px;
  }
  > .el-dialog__body {
    position: static;
    flex: 1;
    min-height: 0;
    padding: 12px 16px 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: var(--sku-req-text);
  }
  .po-request-content {
    height: auto !important;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .po-request-group {
    display: block;
    flex: none;
    max-height: 38vh;
    overflow: auto;
  }
  .one-toolbar {
    flex: none;
    min-width: 0;
    min-height: 32px;
    height: auto;
    line-height: 32px;
    margin: 0 0 12px;
    padding: 0 10px;
    border: 1px solid var(--sku-req-border);
    border-radius: 4px;
    background: #f8fafc;
    color: var(--sku-req-text);
  }
  .one-toolbar .spanRow { color: var(--sku-req-text) !important; font-size: 12px; font-weight: 600; }
  .ivu-form {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px 16px;
    margin-bottom: 16px;
  }
  .ivu-form > span { display: contents; }
  .item-width { width: 100%; height: auto !important; min-height: 32px; margin: 0; }
  .ivu-form-item-label { font-size: 12px; color: var(--sku-req-muted); }
  .po-request-detail-toolbar {
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
    padding-top: 4px;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }
  .po-request-add-detail,
  .po-request-add-detail span,
  .po-request-add-detail i { color: #2563eb !important; }
  .po-request-detail-toolbar > .el-input { max-width: 100%; }
  .po-request-status-gray { color: var(--sku-req-muted) !important; }
  .po-request-content > .el-table {
    flex: 1;
    min-height: 120px;
    margin-top: 0 !important;
    font-size: 12px;
  }
  .el-table th { height: 34px; padding: 0; background: #f8fafc; }
  .el-table th .cell { font-weight: 600; }
  .el-table td { height: 32px; padding: 2px 0; font-size: 12px; }
  .el-table td .cell,
  .el-table th .cell { padding-left: 6px; padding-right: 6px; }
  .custom-table-border .el-table__body td,
  .custom-table-border .el-table__header th { border-color: var(--sku-req-border); }
  .el-table__footer-wrapper td,
  .el-table__fixed-footer-wrapper td { background: #f8fafc; font-weight: 600; }
  .el-input__inner { border-color: var(--sku-req-border); border-radius: 4px; }
  .el-input__inner:focus { border-color: #2563eb; }
  .el-input--medium .el-input__inner { height: 32px; line-height: 32px; }
  .el-input--medium .el-input__icon { line-height: 32px; }
  .el-input--mini .el-input__inner { height: 26px; line-height: 26px; }
  .el-input--mini .el-input__icon { line-height: 26px; }
  .po-request-actions {
    flex: none;
    height: auto !important;
    min-height: 52px;
    margin-top: 10px;
    padding: 10px 0 !important;
    border-top: 1px solid var(--sku-req-border) !important;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .po-request-actions > span { display: flex; gap: 8px; }
  .po-request-actions .el-button {
    height: 32px;
    margin: 0 !important;
    padding: 0 14px;
    border-radius: 4px;
  }
  .po-request-actions .el-button--info { color: #475569; background: #fff; border-color: #cbd5e1; }
  .po-request-actions .el-button--primary { color: #fff; background: #2563eb; border-color: #2563eb; }
  .po-request-actions .el-button.is-disabled { opacity: 0.55; }

  @media (max-width: 1100px) {
    .ivu-form { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 640px) {
    .ivu-form { grid-template-columns: minmax(0, 1fr); }
    > .el-dialog__body { padding-left: 8px; padding-right: 8px; }
  }
}
</style>
